import { Form, Select, Row } from 'antd';
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import ToSRows from './ToSRows';

const isNumeric = (str) => {
  if (typeof str !== 'string') return false;
  // eslint-disable-next-line no-restricted-globals
  return !isNaN(str) && !isNaN(parseFloat(str));
};

const validateCompanySize = (_, value) => {
  if (!value) return Promise.resolve();
  if (!isNumeric(value)) return Promise.reject(new Error('Company Size must be a number'));
  const num = Number.parseInt(value, 10);
  if (num === 0) return Promise.reject(new Error('Company Size cannot be 0'));
  if (num < 0) return Promise.reject(new Error('Company Size must be greater than 0'));

  return Promise.resolve();
};

const validateUsername = (_, value) => {
  if (!value || value.length < 5) return Promise.reject(new Error('Must be 5 or more characters'));
  return Promise.resolve();
};

const COMPANY_TYPE_OPTIONS = [
  { label: 'General Contractor', value: 'generalContractor' },
  { label: 'Service Contractor', value: 'serviceContractor' },
  { label: 'Sub Contractor', value: 'subContractor' },
  { label: 'Other', value: 'other' },
];

const COMPANY_REGION_OPTIONS = [
  { label: 'United States', value: 'us-west-2' },
  { label: 'Canada', value: 'ca-west-1' },
];

export default function GetStartedForm({
  onCreate,
  loading,
}) {
  const [form] = Form.useForm();
  const [acceptedToS, setAcceptedToS] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  const validatePasswordFormat = (_, value) => {
    if (!value) return Promise.resolve();
    if (value.length < 8) return Promise.reject(new Error('Must be 8 or more characters'));

    // eslint-disable-next-line prefer-regex-literals
    const regex = new RegExp(/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/);
    if (!(regex.test(value))) {
      return Promise.reject(new Error('Password must contain: one uppercase character, one lowercase character and one number'));
    }
    form.validateFields(['confirmPassword']);
    return Promise.resolve();
  };

  const compareToFirstPassword = (_, value) => {
    if (!form) return false;
    if (value && value !== form.getFieldValue('password')) {
      return Promise.reject(new Error('Passwords must match'));
    }
    return Promise.resolve();
  };

  const onSubmit = () => {
    form.validateFields().then((values) => {
      onCreate(values);
    });
  };

  return (
    <div className="get-started-form">
      <Form className="login-form" form={form}>
        <div className="login-header">Company Signup</div>
        <div className="login-form-input-label">First Name</div>
        <Form.Item
          name="firstName"
          rules={[
            { required: true, message: 'First Name is required' },
          ]}
          style={{
            width: '100%',
            marginBottom: 20,
            height: 36,
          }}
        >
          <OnTraccrTextInput
            prefixCls="login-redesign-input ant-input"
            placeholder="First Name"
          />
        </Form.Item>
        <div className="login-form-input-label">Last Name</div>
        <Form.Item
          name="lastName"
          rules={[
            { required: true, message: 'Last Name is required' },
          ]}
        >
          <OnTraccrTextInput
            prefixCls="login-redesign-input ant-input"
            placeholder="Last Name"
          />
        </Form.Item>
        <div className="login-form-input-label">Email</div>
        <Form.Item
          name="email"
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email', message: 'Invalid email format' },
          ]}
        >
          <OnTraccrTextInput
            prefixCls="login-redesign-input ant-input"
            placeholder="Email"
          />
        </Form.Item>
        <div className="login-form-input-label">Company Name</div>
        <Form.Item
          name="companyName"
          rules={[
            { required: true, message: 'Company Name is required' },
          ]}
        >
          <OnTraccrTextInput
            prefixCls="login-redesign-input ant-input"
            placeholder="Company Name"
          />
        </Form.Item>
        <div className="login-form-input-label">Company Size</div>
        <Form.Item
          name="companySize"
          rules={[
            { validator: validateCompanySize },
          ]}
        >
          <OnTraccrTextInput
            prefixCls="login-redesign-input ant-input"
            placeholder="Company Size"
            maxLength={10}
          />
        </Form.Item>
        <div className="login-form-input-label">Industry</div>
        <Form.Item
          name="industry"
        >
          <OnTraccrTextInput
            prefixCls="login-redesign-input ant-input"
            placeholder="Industry"
          />
        </Form.Item>
        <div className="login-form-input-label">Company Type</div>
        <Form.Item
          name="companyTypes"
          rules={[
            {
              required: true, message: 'Company type is required',
            },
          ]}
        >
          <Select
            mode="multiple"
            options={COMPANY_TYPE_OPTIONS}
            className="register-select"
            showSearch
            optionFilterProp="label"
            placeholder="Company Type"
          />
        </Form.Item>
        <div className="login-form-input-label">Region</div>
        <Form.Item
          name="companyRegion"
          rules={[
            { required: true, message: 'Company Region is required' },
          ]}
        >
          <Select
            mode="single"
            placeholder="Region"
            options={COMPANY_REGION_OPTIONS}
            className="register-select"
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <div className="login-form-input-label">Username</div>
        <Form.Item
          name="username"
          rules={[
            { required: true, message: 'Username is required' },
            { validator: validateUsername },
          ]}
        >
          <OnTraccrTextInput
            prefixCls="login-redesign-input ant-input"
            placeholder="Username"
          />
        </Form.Item>
        <div className="login-form-input-label">Password</div>
        <Form.Item
          name="password"
          rules={[
            { required: true, message: 'Password is required' },
            { validator: validatePasswordFormat },
          ]}
          style={{
            height: 'auto',
          }}
        >
          <OnTraccrTextInput
            inputPrefixCls="login-redesign-input login-password-input ant-input"
            placeholder="Password"
            password
            type="password"
            style={{ height: 36 }}
          />
        </Form.Item>
        <div className="login-form-input-label">Confirm Password</div>
        <Form.Item
          name="confirmPassword"
          rules={[
            { required: true, message: 'Confirm Password is required' },
            { validator: compareToFirstPassword },
          ]}
          style={{
            height: 'auto',
          }}
        >
          <OnTraccrTextInput
            inputPrefixCls="login-redesign-input login-password-input ant-input"
            placeholder="Confirm Password"
            password
            type="password"
            style={{ height: 36 }}
          />
        </Form.Item>
        <ToSRows
          style={{ marginTop: 10, marginBottom: 10 }}
          acceptedToS={acceptedToS}
          acceptedPrivacy={acceptedPrivacy}
          setAcceptedToS={setAcceptedToS}
          setAcceptedPrivacy={setAcceptedPrivacy}
        />
        <Row justify="center">
          <OnTraccrButton
            isRedesign
            title="Submit"
            style={{ width: '100%', fontWeight: 500, marginTop: 20, marginBottom: 20 }}
            htmlType="submit"
            onClick={onSubmit}
            loading={loading}
            disabled={!acceptedToS || !acceptedPrivacy}
          />
        </Row>
      </Form>
    </div>
  );
}

GetStartedForm.propTypes = {
  onCreate: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};
